import React, { useRef, useState, useCallback } from "react";
import Webcam from "react-webcam";

function CameraCapture({ onCapture }) {
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState("camera"); // "camera" | "upload"
  const [cameraError, setCameraError] = useState(false);
  const [isMirrored, setIsMirrored] = useState(true);
  const [facingMode, setFacingMode] = useState("user");

  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) return;

    fetch(imageSrc)
      .then((r) => r.blob())
      .then((blob) => onCapture([blob], [imageSrc]));
  }, [onCapture]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    onCapture([file], [previewUrl]);
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    // Usually, the back camera shouldn't be mirrored
    if (facingMode === "user") {
      setIsMirrored(false);
    } else {
      setIsMirrored(true);
    }
  };

  return (
    <div className="glass-card">
      <div className="flex gap-2 mb-6 bg-bg3 p-1.5 rounded-xl border border-borderBase">
        <button
          className={`flex-1 flex items-center justify-center gap-2 border-none bg-transparent p-2.5 rounded-lg cursor-pointer transition-all text-[0.95rem] font-medium ${mode === "camera" ? "bg-accent-gradient text-white shadow-glow" : "text-textMuted hover:bg-white/5 hover:text-white"}`}
          onClick={() => setMode("camera")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
          Camera
        </button>
        <button
          className={`flex-1 flex items-center justify-center gap-2 border-none bg-transparent p-2.5 rounded-lg cursor-pointer transition-all text-[0.95rem] font-medium ${mode === "upload" ? "bg-accent-gradient text-white shadow-glow" : "text-textMuted hover:bg-white/5 hover:text-white"}`}
          onClick={() => setMode("upload")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
          Upload
        </button>
      </div>

      {mode === "camera" && (
        <div className="relative rounded-xl overflow-hidden bg-black border border-borderBase">
          {cameraError ? (
            <div className="p-10 text-center text-textMuted">
              <p className="mb-4">Camera not available.</p>
              <button 
                className="bg-bg3 text-white border border-borderBase px-6 py-3 rounded-full text-[0.95rem] font-medium cursor-pointer transition-all hover:bg-white/5 hover:border-borderHover" 
                onClick={() => setMode("upload")}
              >
                Upload instead
              </button>
            </div>
          ) : (
            <>
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{ width: 640, height: 480, facingMode }}
                onUserMediaError={() => setCameraError(true)}
                className="block w-full rounded-xl"
                mirrored={isMirrored}
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[180px] h-[240px] border-2 border-white/40 rounded-full shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]" />
              </div>
              <div className="absolute top-4 right-4 flex flex-col gap-3">
                <button 
                  className="bg-black/60 border border-white/30 text-white w-11 h-11 rounded-full cursor-pointer flex items-center justify-center backdrop-blur-sm z-10 transition-all hover:bg-accentSolid hover:scale-110 hover:shadow-glow" 
                  onClick={toggleCamera}
                  title="Flip camera"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
                </button>
                <button 
                  className={`bg-black/60 border ${isMirrored ? "border-accentSolid text-accentSolid" : "border-white/30 text-white"} w-11 h-11 rounded-full cursor-pointer flex items-center justify-center backdrop-blur-sm z-10 transition-all hover:bg-accentSolid hover:text-white hover:scale-110 hover:shadow-glow hover:border-accentSolid`}
                  onClick={() => setIsMirrored(!isMirrored)}
                  title="Mirror camera"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M3 7l5 5-5 5V7zM21 7l-5 5 5 5V7z"/></svg>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {mode === "camera" && !cameraError && (
        <button 
          className="flex items-center justify-center gap-3 w-full p-[18px] bg-gradient-to-br from-[#00d2ff] to-[#3a7bd5] text-white border-none rounded-xl text-[1.3rem] font-extrabold cursor-pointer transition-all shadow-[0_8px_30px_rgba(0,210,255,0.5)] mt-5 uppercase tracking-[1px] hover:-translate-y-[3px] hover:shadow-[0_12px_40px_rgba(0,210,255,0.7)] hover:from-[#3a7bd5] hover:to-[#00d2ff] active:translate-y-px" 
          onClick={handleCapture}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
          Take Photo
        </button>
      )}

      {mode === "upload" && (
        <div>
          <div
            className="border-2 border-dashed border-borderBase rounded-xl py-[60px] px-5 text-center cursor-pointer transition-all bg-black/20 hover:border-accentSolid hover:bg-[rgba(123,97,255,0.05)]"
            onClick={() => fileInputRef.current?.click()}
          >
            <span className="text-5xl block mb-4">🖼️</span>
            <p className="text-[1.1rem] font-medium mb-2">Click to select a photo</p>
            <small className="text-textMuted">JPG, PNG, WEBP supported</small>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
        </div>
      )}

      <p className="mt-5 text-[0.9rem] text-textMuted text-center">
        Tip: Use a clear, front-facing photo with good lighting for best results.
      </p>
    </div>
  );
}

export default CameraCapture;
